import { API_CONFIG, apiUtils } from "../config.js";

// Attendance Management API
export const attendanceApi = {
  // Mark attendance for single student
  // attendanceData should contain: { studentEnrollmentId, status, date, isHoliday }
  markAttendance: async (attendanceData) => {
    const url = `${API_CONFIG.BASE_URL}/attendance/mark`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(attendanceData),
    });
  },

  // Bulk mark attendance
  // bulkData should contain: { date, isHoliday, records: [{ studentEnrollmentId, status }] }
  bulkMarkAttendance: async (bulkData) => {
    const url = `${API_CONFIG.BASE_URL}/attendance/bulk-mark`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(bulkData),
    });
  },

  // Get student attendance range
  getStudentAttendance: async (studentRegistrationId, fromDate, toDate) => {
    const queryParams = apiUtils.buildQueryString({
      from: fromDate,
      to: toDate,
    });
    const url = `${API_CONFIG.BASE_URL}/attendance/student/${studentRegistrationId}?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get day attendance
  getDayAttendance: async (date, unitId, academicYear, sectionId) => {
    let url = `${API_CONFIG.BASE_URL}/attendance/day?date=${date}&unitId=${unitId}`;
    if (academicYear) {
      url += `&academicYear=${academicYear}`;
    }
    if (sectionId) {
      url += `&sectionId=${sectionId}`;
    }
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get class attendance
  getClassAttendance: async (classId, date) => {
    const queryParams = apiUtils.buildQueryString({ classId, date });
    const url = `${API_CONFIG.BASE_URL}/attendance/class?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get unit attendance for a date range
  // Example: /attendance/unit/:unitId?from=YYYY-MM-DD&to=YYYY-MM-DD
  getUnitAttendance: async (unitId, fromDate, toDate, academicYear) => {
    const queryParams = apiUtils.buildQueryString({
      from: fromDate,
      to: toDate,
      academicYear
    });
    const url = `${API_CONFIG.BASE_URL}/attendance/unit/${unitId}?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get attendance statistics
  getAttendanceStats: async (filters = {}) => {
    const queryParams = apiUtils.buildQueryString(filters);
    const url = `${API_CONFIG.BASE_URL}/attendance/stats?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Update attendance
  updateAttendance: async (attendanceId, attendanceData) => {
    const url = `${API_CONFIG.BASE_URL}/attendance/update/${attendanceId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(attendanceData),
    });
  },

  // Delete attendance
  deleteAttendance: async (attendanceId) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/attendance/delete/${attendanceId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // Mark a day as holiday for all students
  markHoliday: async (holidayData) => {
    const url = `${API_CONFIG.BASE_URL}/attendance/mark-holiday`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(holidayData),
    });
  },

  // Get student statistics (excluding holidays)
  getStudentStatistics: async (studentRegistrationId, fromDate, toDate) => {
    const queryParams = apiUtils.buildQueryString({
      from: fromDate,
      to: toDate,
    });
    const url = `${API_CONFIG.BASE_URL}/attendance/student/${studentRegistrationId}/statistics?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get holidays in a date range
  getHolidays: async (fromDate, toDate) => {
    const queryParams = apiUtils.buildQueryString({
      from: fromDate,
      to: toDate,
    });
    const url = `${API_CONFIG.BASE_URL}/attendance/holidays?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Send WhatsApp notifications
  sendNotifications: async (notificationData) => {
    const url = `${API_CONFIG.BASE_URL}/attendance/send-notifications`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(notificationData),
    });
  },

  // Get attendance overview
  getAttendanceOverview: async (date, academicYear) => {
    const queryParams = apiUtils.buildQueryString({
      date,
      academicYear,
    });
    const url = `${API_CONFIG.BASE_URL}/attendance/overview?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },
};

export default attendanceApi;
